// ReviewsAdminTab.jsx — Admin Panel "Reviews" sub-tab, extracted from
// LimitedPartnerPortal. Live Review two-pane meeting mode (desktop) plus the
// classic filterable list view, the Bayesian-weighted review scoring memos,
// admin notes CRUD, shortlist + winner toggles, and CSV export. Filter and
// notes state stays lifted in the portal so settings survive sub-tab
// switches; MultiSelectDropdown / formatDate / isMobile are shared with the
// Review Pitches tab and flow in as props.
import React, { useMemo, useCallback } from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../../firebaseConfig.js";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import {
  updateWinnerPredictions,
  reverseWinnerPredictions,
  getCurrentQuarter,
} from "../../../../services/statsTracking";
import { RetroButton, RetroPill, EmptyState } from "../../../ui/retro";
import { ratingTonePill } from "../../../../helpers/labels";
import { isManualAward } from "../../../../helpers/manualAward";
import ReviewRatingIcon from "../../../icons/ReviewRatingIcon";
import StatusIcon from "../../../icons/StatusIcon";
import LiveReviewPane from "../../review/LiveReviewPane";

// Admin-only LP rating weights. Shown in the admin review summary so we can rank pitches,
// deliberately NOT exposed on the public review page so reviewers aren't anchored by prior votes.
const LP_RATING_WEIGHTS = { Favorite: 2, Consideration: 1, Pass: 0, Ineligible: -2 };

// Bayesian-shrinkage prior: number of "pseudo reviews" to blend in at the global
// mean. Stops a single Favorite (raw avg +2.00) from outranking a pitch with five
// Favorites and a Consideration (raw avg +1.83). Higher = stronger pull toward the
// mean for low-coverage pitches; ~3 matches our typical 3–8 reviewers per pitch.
const LP_SHRINKAGE_PRIOR = 3;
export default function ReviewsAdminTab({
  user,
  users,
  chapters,
  isAdmin,
  isSuperAdmin,
  userChapter,
  reviews,
  adminPitches,
  setAdminPitches,
  allReviewsData,
  adminNotes,
  setAdminNotes,
  adminNoteDrafts,
  setAdminNoteDrafts,
  editingAdminNoteId,
  setEditingAdminNoteId,
  editingAdminNoteText,
  setEditingAdminNoteText,
  adminNoteSavingId,
  setAdminNoteSavingId,
  shortlistTogglingId,
  setShortlistTogglingId,
  adminChapterFilter,
  setAdminChapterFilter,
  adminQuarterFilter,
  setAdminQuarterFilter,
  adminSearch,
  setAdminSearch,
  adminHidePassed,
  setAdminHidePassed,
  adminFavoriteFilterMode,
  setAdminFavoriteFilterMode,
  adminSortMode,
  setAdminSortMode,
  expandedPitchId,
  setExpandedPitchId,
  focusedPitchId,
  setFocusedPitchId,
  detailsDrawerOpen,
  setDetailsDrawerOpen,
  loadAdminData,
  loadLPData,
  MultiSelectDropdown,
  formatDate,
  isMobile,
  showAppAlert,
  showAppConfirm,
}) {
// Global mean of every rated review in the system. Used as the prior in the
// Bayesian-weighted average so low-coverage pitches don't dominate the sort.
// Recomputes whenever reviews change.
const globalMeanScore = useMemo(() => {
  if (!Array.isArray(allReviewsData) || allReviewsData.length === 0) return 0;
  let sum = 0;
  let n = 0;
  for (const r of allReviewsData) {
    const rating = r.overallLpRating;
    if (rating in LP_RATING_WEIGHTS) {
      sum += LP_RATING_WEIGHTS[rating];
      n++;
    }
  }
  return n > 0 ? sum / n : 0;
}, [allReviewsData]);

// Index reviews by pitchId once per allReviewsData change. The admin reviews
// tab calls getGroupedReviewsForAdmin once per row (and again per sort
// comparator), so a per-call .filter() on the full reviews array becomes
// O(pitches × reviews) — at 1k pitches and 5k reviews that's 5M ops per render.
const reviewsByPitch = useMemo(() => {
  const map = new Map();
  if (!Array.isArray(allReviewsData)) return map;
  for (const review of allReviewsData) {
    const pitchId = review?.pitchId;
    if (!pitchId) continue;
    const list = map.get(pitchId);
    if (list) list.push(review);
    else map.set(pitchId, [review]);
  }
  return map;
}, [allReviewsData]);

// Same idea for the users lookup that runs inside getGroupedReviewsForAdmin —
// users.find() per review is O(reviews × users), which compounds with the
// outer call pattern.
const usersById = useMemo(() => {
  const map = new Map();
  if (!Array.isArray(users)) return map;
  for (const u of users) {
    const id = u.uid || u.id;
    if (id) map.set(id, u);
  }
  return map;
}, [users]);

// Memoized function to group reviews for a specific pitch in the admin view
const getGroupedReviewsForAdmin = useCallback((pitchId) => {
  // Ensure dependencies (allReviewsData, users) are available
  if (!Array.isArray(allReviewsData) || !Array.isArray(users)) {
    return { count: 0, byRating: {}, details: [], comments: [] };
  }

  const relevantReviews = reviewsByPitch.get(pitchId) || [];
  const count = relevantReviews.length;
  if (count === 0) return { count: 0, byRating: {}, details: [], comments: [], score: 0, scoredCount: 0, averageScore: null, weightedAverage: null };

  const allComments = [];

  const groupedByRating = relevantReviews.reduce((acc, review) => {
    const rating = review.overallLpRating || 'No Rating';
    if (!acc[rating]) {
      acc[rating] = { count: 0, reviewers: [] };
    }
    acc[rating].count++;

    // Find reviewer's name using the 'users' state
    const reviewerInfo = usersById.get(review.reviewerId);
    const reviewerDisplay = reviewerInfo ? (reviewerInfo.name || reviewerInfo.email) : (review.reviewerName || `ID: ${review.reviewerId?.substring(0,6)}...` || 'Unknown Reviewer');
    acc[rating].reviewers.push(reviewerDisplay);

    // Collect comment with reviewer info
    if (review.comments && review.comments.trim() !== "") {
        allComments.push({ text: review.comments.trim(), reviewer: reviewerDisplay });
    }

    return acc;
  }, {});

  // Prepare detailed list for potential expansion later (remains unchanged)
  const detailedReviews = relevantReviews.map(review => {
    const reviewerInfo = usersById.get(review.reviewerId);
    const reviewerDisplay = reviewerInfo ? (reviewerInfo.name || reviewerInfo.email) : (review.reviewerName || `ID: ${review.reviewerId?.substring(0,6)}...` || 'Unknown Reviewer');
    return {
      ...review,
      reviewerDisplay: reviewerDisplay,
      // Use robust formatDate for the raw submittedAt value
      submittedDate: formatDate(review.submittedAt),
    };
  }).sort((a,b) => {
    // Need robust comparison in case dates are invalid
    const timeA = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : (a.submittedAt instanceof Date ? a.submittedAt.getTime() : 0);
    const timeB = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : (b.submittedAt instanceof Date ? b.submittedAt.getTime() : 0);
    return (timeB || 0) - (timeA || 0); // Sort newest first
  });

  // Weighted score: Favorite +2, Consideration +1, Pass 0, Ineligible -2.
  // "No Rating" rows are ignored so partial reviews don't skew the average.
  let score = 0;
  let scoredCount = 0;
  Object.entries(groupedByRating).forEach(([rating, data]) => {
    if (rating in LP_RATING_WEIGHTS) {
      score += LP_RATING_WEIGHTS[rating] * data.count;
      scoredCount += data.count;
    }
  });
  const averageScore = scoredCount > 0 ? score / scoredCount : null;
  // Bayesian-weighted average: blends raw avg with the global mean, weighted by
  // how many actual reviews back this pitch. Few reviews → pulled toward mean.
  // Many reviews → trusts the raw avg. Formula: (n*rawAvg + m*C) / (n+m), which
  // here simplifies because score === scoredCount * averageScore.
  const weightedAverage = scoredCount > 0
    ? (score + LP_SHRINKAGE_PRIOR * globalMeanScore) / (scoredCount + LP_SHRINKAGE_PRIOR)
    : null;

  return {
    count: count,
    byRating: groupedByRating,
    details: detailedReviews,
    comments: allComments,
    score,
    scoredCount,
    averageScore,
    weightedAverage
  };
}, [allReviewsData, users, reviewsByPitch, usersById, globalMeanScore]);

// Group admin notes by pitchId for render. Sorted newest-first within each pitch
// so the live discussion always shows the most recent thought at the top.
const adminNotesByPitch = useMemo(() => {
  const grouped = {};
  for (const note of adminNotes) {
    if (!note?.pitchId) continue;
    if (!grouped[note.pitchId]) grouped[note.pitchId] = [];
    grouped[note.pitchId].push(note);
  }
  const getTime = (ts) => {
    if (!ts) return 0;
    if (typeof ts.toDate === 'function') return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    return 0;
  };
  Object.values(grouped).forEach(list => {
    list.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));
  });
  return grouped;
}, [adminNotes]);

// Memoized list of Admin pitches filtered AND sorted by current UI settings
const adminFilteredSortedPitches = useMemo(() => {
  if (!Array.isArray(adminPitches)) return [];

  let filtered = adminPitches.filter((p) => {
    // Manually logged grant awards (Grant awards tab) are not applications —
    // they never appear in the review pipeline.
    if (isManualAward(p)) return false;

    const groupedReviews = getGroupedReviewsForAdmin(p.id); // Get review summary once

    // Filter by Quarter
    const matchesQuarter = adminQuarterFilter.length === 0 || adminQuarterFilter.includes(p.quarter);
    if (!matchesQuarter) return false;

    // Filter by Chapter (only if SuperAdmin is filtering)
    const matchesChapter = !isSuperAdmin || !adminChapterFilter || p.chapter === adminChapterFilter;
    if (!matchesChapter) return false;

    // Filter by Admin Search Term (Business, Founder, Email)
    const adminSearchLower = adminSearch.toLowerCase();
    const matchesAdminSearch = !adminSearchLower ||
                               p.businessName?.toLowerCase().includes(adminSearchLower) ||
                               p.founderName?.toLowerCase().includes(adminSearchLower) ||
                               p.email?.toLowerCase().includes(adminSearchLower);
    if (!matchesAdminSearch) return false;

    // Filter by "Hide Passed" Toggle
    // Hide if there are reviews, and ALL reviews are 'Pass' or 'Ineligible'
    const allReviewsArePassOrIneligible = groupedReviews.count > 0 &&
                                          Object.keys(groupedReviews.byRating).every(r => r === 'Pass' || r === 'Ineligible');
    if (adminHidePassed && allReviewsArePassOrIneligible) {
      return false;
    }

    // Filter by Favorites / Considerations (using dropdown state)
    const favoriteCount = groupedReviews.byRating?.["Favorite"]?.count || 0;
    const considerationCount = groupedReviews.byRating?.["Consideration"]?.count || 0;

    if (adminFavoriteFilterMode === 'favsOnly' && favoriteCount === 0) {
      return false;
    }
    if (adminFavoriteFilterMode === 'favsAndCons' && favoriteCount === 0 && considerationCount === 0) {
      return false;
    }
    if (adminFavoriteFilterMode === 'shortlisted' && !p.shortlisted) {
      return false;
    }

    // Passed all admin filters
    return true;
  });

  // --- Sorting Logic ---
  const submittedTime = (p) => (
    p.createdAt?.toDate ? p.createdAt.toDate().getTime()
      : (p.createdAt instanceof Date ? p.createdAt.getTime() : 0)
  );

  // Favorite-filter precedence: if the user is filtering by favorites,
  // sort by favorite count regardless of sort dropdown.
  if (adminFavoriteFilterMode === 'favsOnly' || adminFavoriteFilterMode === 'favsAndCons') {
    filtered.sort((a, b) => {
      const favCountA = getGroupedReviewsForAdmin(a.id).byRating?.["Favorite"]?.count || 0;
      const favCountB = getGroupedReviewsForAdmin(b.id).byRating?.["Favorite"]?.count || 0;
      if (favCountB !== favCountA) return favCountB - favCountA;
      return (submittedTime(b) || 0) - (submittedTime(a) || 0);
    });
  } else if (adminSortMode === 'oldest') {
    filtered.sort((a, b) => submittedTime(a) - submittedTime(b));
  } else if (adminSortMode === 'sumDesc' || adminSortMode === 'sumAsc') {
    // Raw score (sum). Rewards volume of favorable reviews. Unreviewed = 0.
    filtered.sort((a, b) => {
      const scoreA = getGroupedReviewsForAdmin(a.id).score || 0;
      const scoreB = getGroupedReviewsForAdmin(b.id).score || 0;
      const diff = adminSortMode === 'sumDesc' ? scoreB - scoreA : scoreA - scoreB;
      if (diff !== 0) return diff;
      return (submittedTime(b) || 0) - (submittedTime(a) || 0);
    });
  } else if (adminSortMode === 'avgDesc' || adminSortMode === 'avgAsc') {
    // Bayesian-weighted average. Pitches with few reviews are pulled toward the
    // global mean so a single +2 rave doesn't outrank a well-reviewed pitch.
    // Tiebreakers: more reviews first (higher confidence), then newest.
    filtered.sort((a, b) => {
      const gA = getGroupedReviewsForAdmin(a.id);
      const gB = getGroupedReviewsForAdmin(b.id);
      const avgA = gA.weightedAverage === null || gA.weightedAverage === undefined ? 0 : gA.weightedAverage;
      const avgB = gB.weightedAverage === null || gB.weightedAverage === undefined ? 0 : gB.weightedAverage;
      const diff = adminSortMode === 'avgDesc' ? avgB - avgA : avgA - avgB;
      if (diff !== 0) return diff;
      const reviewDiff = (gB.scoredCount || 0) - (gA.scoredCount || 0);
      if (reviewDiff !== 0) return reviewDiff;
      return (submittedTime(b) || 0) - (submittedTime(a) || 0);
    });
  } else if (adminSortMode === 'mostReviews') {
    filtered.sort((a, b) => {
      const countA = getGroupedReviewsForAdmin(a.id).count || 0;
      const countB = getGroupedReviewsForAdmin(b.id).count || 0;
      if (countB !== countA) return countB - countA;
      return (submittedTime(b) || 0) - (submittedTime(a) || 0);
    });
  } else {
    // 'newest' (default)
    filtered.sort((a, b) => (submittedTime(b) || 0) - (submittedTime(a) || 0));
  }

  return filtered;

}, [
    adminPitches,
    adminQuarterFilter,
    adminChapterFilter,
    adminSearch,
    isSuperAdmin,
    adminHidePassed,
    adminFavoriteFilterMode,
    adminSortMode,
    getGroupedReviewsForAdmin
]);

// Toggle the group "shortlist" flag on a pitch. Used by chapter directors during
// the live review meeting to bookmark candidates for deeper discussion. The flag
// lives directly on the pitch doc; only admins can toggle it (rules allow admin
// updates on any pitch field).
const handleToggleShortlist = async (pitchId, currentShortlisted) => {
  if (!isAdmin) return;
  setShortlistTogglingId(pitchId);
  const nextValue = !currentShortlisted;
  try {
    await updateDoc(doc(db, "pitches", pitchId), {
      shortlisted: nextValue,
      shortlistedAt: nextValue ? serverTimestamp() : null,
      shortlistedBy: nextValue ? (user.name || user.email || user.uid) : null,
    });
    // Optimistic local update so the UI reflects the change before the next reload.
    setAdminPitches(prev => prev.map(p => p.id === pitchId
      ? { ...p, shortlisted: nextValue, shortlistedBy: nextValue ? (user.name || user.email || user.uid) : null }
      : p
    ));
  } catch (error) {
    console.error(`LPPortal: Shortlist toggle failed for pitch ${pitchId}:`, error);
    showAppAlert(`Could not update shortlist: ${error.message}`);
  } finally {
    setShortlistTogglingId(null);
  }
};

// Admin notes CRUD. Notes are stored in /adminNotes and attributed to the signed-in
// admin. Each note carries enough scoping metadata (pitchId, chapter, quarter) to
// support chapter-scoped list queries and future export.
const handleAddAdminNote = async (pitch) => {
  if (!isAdmin || !pitch?.id) return;
  const draft = (adminNoteDrafts[pitch.id] || "").trim();
  if (!draft) return;
  setAdminNoteSavingId(pitch.id);
  try {
    const docRef = await addDoc(collection(db, "adminNotes"), {
      pitchId: pitch.id,
      pitchBusinessName: pitch.businessName || "",
      chapter: pitch.chapter || userChapter || "",
      quarter: pitch.quarter || "",
      authorId: user.uid,
      authorName: user.name || user.email || "",
      text: draft,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      editCount: 0,
    });
    // Optimistic: prepend the new note so it shows up instantly.
    setAdminNotes(prev => [{
      id: docRef.id,
      pitchId: pitch.id,
      pitchBusinessName: pitch.businessName || "",
      chapter: pitch.chapter || userChapter || "",
      quarter: pitch.quarter || "",
      authorId: user.uid,
      authorName: user.name || user.email || "",
      text: draft,
      createdAt: new Date(),
      updatedAt: new Date(),
      editCount: 0,
    }, ...prev]);
    setAdminNoteDrafts(prev => ({ ...prev, [pitch.id]: "" }));
  } catch (error) {
    console.error(`LPPortal: Add admin note failed for pitch ${pitch.id}:`, error);
    showAppAlert(`Could not save note: ${error.message}`);
  } finally {
    setAdminNoteSavingId(null);
  }
};

const handleStartEditAdminNote = (note) => {
  setEditingAdminNoteId(note.id);
  setEditingAdminNoteText(note.text || "");
};

const handleCancelEditAdminNote = () => {
  setEditingAdminNoteId(null);
  setEditingAdminNoteText("");
};

const handleSaveEditAdminNote = async (noteId) => {
  const trimmed = editingAdminNoteText.trim();
  if (!trimmed) return;
  const note = adminNotes.find(n => n.id === noteId);
  if (!note) return;
  setAdminNoteSavingId(noteId);
  try {
    await updateDoc(doc(db, "adminNotes", noteId), {
      text: trimmed,
      updatedAt: serverTimestamp(),
      editCount: (note.editCount || 0) + 1,
    });
    setAdminNotes(prev => prev.map(n => n.id === noteId
      ? { ...n, text: trimmed, updatedAt: new Date(), editCount: (n.editCount || 0) + 1 }
      : n
    ));
    handleCancelEditAdminNote();
  } catch (error) {
    console.error(`LPPortal: Edit admin note failed for ${noteId}:`, error);
    showAppAlert(`Could not update note: ${error.message}`);
  } finally {
    setAdminNoteSavingId(null);
  }
};

const handleDeleteAdminNote = async (noteId) => {
  if (!showAppConfirm("Delete this note? This cannot be undone.")) return;
  setAdminNoteSavingId(noteId);
  try {
    await deleteDoc(doc(db, "adminNotes", noteId));
    setAdminNotes(prev => prev.filter(n => n.id !== noteId));
    if (editingAdminNoteId === noteId) handleCancelEditAdminNote();
  } catch (error) {
    console.error(`LPPortal: Delete admin note failed for ${noteId}:`, error);
    showAppAlert(`Could not delete note: ${error.message}`);
  } finally {
    setAdminNoteSavingId(null);
  }
};

const handleAssignWinner = async (pitchId, currentWinnerStatus) => {
  const pitchToUpdate = adminPitches.find(p => p.id === pitchId);
  if (!pitchToUpdate) {
    console.error(`LPPortal: Assign Winner failed - Pitch ${pitchId} not found in adminPitches state.`);
    showAppAlert("Error: Could not find pitch data to update.");
    return;
  }

  // Per-chapter grant default, falling back to the historical $1,000 when no
  // chapter-level override is set. Pitch-level awardAmount always wins over both.
  const chapterDoc = chapters.find(c => c.name === pitchToUpdate.chapter);
  const chapterDefault = Number.isFinite(chapterDoc?.defaultGrantAmount) && chapterDoc.defaultGrantAmount > 0
    ? chapterDoc.defaultGrantAmount
    : 1000;

  if (!currentWinnerStatus) {
    // Promoting to winner — capture award amount (blank accepts the chapter default).
    const input = window.prompt(
      `Enter grant amount for "${pitchToUpdate.businessName || 'this pitch'}" in dollars.\nLeave blank to use the chapter default ($${chapterDefault.toLocaleString()}).`,
      ''
    );
    if (input === null) return; // user cancelled prompt
    const trimmed = input.trim();
    const parsed = trimmed === '' ? chapterDefault : Number(trimmed.replace(/[$,]/g, ''));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showAppAlert('Award amount must be a positive number.');
      return;
    }
    const amount = Math.round(parsed);
    try {
      // Newly-assigned winners default to hidden — chapters publish from the
      // Grant awards tab when they're ready to announce. Existing winners
      // (no field) read as published; only docs explicitly set to false hide.
      await updateDoc(doc(db, "pitches", pitchId), {
        isWinner: true,
        awardAmount: amount,
        awardedAt: serverTimestamp(),
        winnerPublished: false
      });
      await updateWinnerPredictions(pitchId, pitchToUpdate);
      showAppAlert(`Winner marked: "${pitchToUpdate.businessName}" at $${amount.toLocaleString()}. Not yet live on the website: toggle "Live on website" in the Grant awards tab to publish.`);
      loadLPData();
      loadAdminData();
    } catch (error) {
      console.error(`LPPortal: Assign Winner Error for pitch ${pitchId}:`, error);
      if (error.code === 'permission-denied' || (error.message || '').includes('permission-denied')) {
        showAppAlert(`Update failed: Permission Denied. Check Firestore rules for updating pitch winner status.`);
      } else {
        showAppAlert(`Update failed: ${error.message}. Check Firestore Rules.`);
      }
    }
    return;
  }

  // Un-marking an existing winner.
  if (!showAppConfirm(`Remove winner status for "${pitchToUpdate.businessName || 'Unknown'}"?\nThis also clears the award amount and adjusts LP prediction stats.`)) return;
  try {
    await updateDoc(doc(db, "pitches", pitchId), {
      isWinner: false,
      awardAmount: deleteField(),
      awardedAt: deleteField(),
      winnerPublished: deleteField()
    });
    // Reverse the stat bumps updateWinnerPredictions applied when this pitch was marked.
    // Prior behavior left these stale on un-mark.
    await reverseWinnerPredictions(pitchId);
    showAppAlert(`"${pitchToUpdate.businessName}" is no longer marked as a winner.`);
    loadLPData();
    loadAdminData();
  } catch (error) {
    console.error(`LPPortal: Remove Winner Error for pitch ${pitchId}:`, error);
    if (error.code === 'permission-denied' || (error.message || '').includes('permission-denied')) {
      showAppAlert(`Update failed: Permission Denied. Check Firestore rules for updating pitch winner status.`);
    } else {
      showAppAlert(`Update failed: ${error.message}. Check Firestore Rules.`);
    }
  }
};


const handleAdminPitchExport = () => {
  const filteredPitchesToExport = adminFilteredSortedPitches;
  if (filteredPitchesToExport.length === 0) {
    showAppAlert("No pitches match the current filters to export.");
    return;
  }

  const rows = filteredPitchesToExport.map((p) => {
    const groupedReviews = getGroupedReviewsForAdmin(p.id);
    return {
      "Pitch ID": p.id,
      "Business Name": p.businessName || "",
      "Founder Name": p.founderName || "",
      "Email": p.email || "",
      "Chapter": p.chapter || "",
      "Quarter": p.quarter || "",
      // Use robust formatDate for the raw createdAt value
      "Submitted Date": formatDate(p.createdAt || p.createdDate),
      "Is Winner": p.isWinner ? "Yes" : "No",
      "Value Proposition": p.valueProp || "",
      "Problem": p.problem || "",
      "Solution": p.solution || "",
      "Business Model": p.businessModel || "",
      "Paying Customers": p.hasPayingCustomers ? "Yes" : "No",
      "Grant Use Plan": p.grantUsePlan || "",
      "Zip Code": p.zipCode || "",
      "Self Identification": p.selfIdentification?.join(", ") || "",
      "Website": p.website || "",
      "Heard About": p.heardAbout || "",
      "Terms & Privacy Agreed": p.consentToShare ? "Yes" : "No",
      "In-Person Meetup Agreed": p.consentToMeetup ? "Yes" : "No",
      "Pitch Video URL": p.pitchVideoUrl || "",
      "Pitch Video File": p.pitchVideoFile || "",
      "Total Reviews": groupedReviews.count || 0,
      "Rated Reviews": groupedReviews.scoredCount || 0,
      "Favorite Count": groupedReviews.byRating?.["Favorite"]?.count || 0,
      "Consideration Count": groupedReviews.byRating?.["Consideration"]?.count || 0,
      "Pass Count": groupedReviews.byRating?.["Pass"]?.count || 0,
      "Ineligible Count": groupedReviews.byRating?.["Ineligible"]?.count || 0,
      "Total Score": groupedReviews.scoredCount > 0 ? groupedReviews.score : "",
      "Raw Avg": groupedReviews.averageScore != null ? groupedReviews.averageScore.toFixed(3) : "",
      "Weighted Avg": groupedReviews.weightedAverage != null ? groupedReviews.weightedAverage.toFixed(3) : "",
      // Add more fields as needed
    };
  });

  try {
    const csv = Papa.unparse(rows, { header: true });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    // Generate filename based on filters (using new dropdown state)
    const timestamp = new Date().toISOString().slice(0, 10);
    const chapterPart = adminChapterFilter || 'allChapters';
    const quarterPart = adminQuarterFilter.length === 0 ? 'allQuarters' : adminQuarterFilter.join('-');
    const favFilterPart = adminFavoriteFilterMode === 'favsOnly' ? '_favsOnly' : (adminFavoriteFilterMode === 'favsAndCons' ? '_favsAndCons' : '');
    const filename = `neighborhoodOS_pitches_${chapterPart}_${quarterPart}${favFilterPart}_${timestamp}.csv`;

    saveAs(blob, filename);
  } catch (error) {
    console.error("LPPortal: CSV Export Error:", error);
    showAppAlert(`CSV export failed: ${error.message}`);
  }
};

            // Compute hero stats once so they render in the page header rather
            // than being buried at the bottom of a filter toolbar.
            const totalPitches = adminFilteredSortedPitches.length;
            const totalReviews = allReviewsData.length;
            const reviewedByMe = Object.keys(reviews || {}).length;
            // Mobile coerces to List — Live Review is a desktop/projector workflow
            // (two-pane queue + detail layout doesn't work below tablet width).
            const effectiveMode = isMobile() ? 'list' : 'live';
            const paneHeader = (
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">
                    {getCurrentQuarter()} · {isSuperAdmin ? (adminChapterFilter || "All chapters") : (userChapter || "All chapters")}
                  </div>
                  <h2 className="win95-pane-header__title">Reviews</h2>
                </div>
                <div className="win95-pane-header__stats">
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Pitches shown</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-magenta-deep)" }}>
                      {totalPitches}
                    </div>
                  </div>
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">LP reviews</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-aqua-deep)" }}>
                      {totalReviews}
                    </div>
                  </div>
                  <div className="win95-statpanel">
                    <div className="win95-statpanel__label">Reviewed by you</div>
                    <div className="win95-statpanel__figure" style={{ color: "var(--mb-tangerine-deep)" }}>
                      {reviewedByMe}
                    </div>
                  </div>
                </div>
              </div>
            );
            if (effectiveMode === 'live') {
              return (
                <>
                  {paneHeader}
                  <LiveReviewPane
                    pitches={adminFilteredSortedPitches}
                    focusedPitchId={focusedPitchId}
                    setFocusedPitchId={setFocusedPitchId}
                    detailsDrawerOpen={detailsDrawerOpen}
                    setDetailsDrawerOpen={setDetailsDrawerOpen}
                    formatDate={formatDate}
                    getGroupedReviewsForAdmin={getGroupedReviewsForAdmin}
                    actionProps={{
                      handleToggleShortlist,
                      handleAssignWinner,
                      shortlistTogglingId,
                    }}
                    notesProps={{
                      user,
                      isSuperAdmin,
                      adminNotesByPitch,
                      adminNoteDrafts,
                      setAdminNoteDrafts,
                      editingAdminNoteId,
                      editingAdminNoteText,
                      setEditingAdminNoteText,
                      adminNoteSavingId,
                      handleAddAdminNote,
                      handleStartEditAdminNote,
                      handleSaveEditAdminNote,
                      handleCancelEditAdminNote,
                      handleDeleteAdminNote,
                    }}
                    filtersProps={{
                      isSuperAdmin,
                      adminPitches,
                      adminChapterFilter,
                      setAdminChapterFilter,
                      adminQuarterFilter,
                      setAdminQuarterFilter,
                      adminFavoriteFilterMode,
                      setAdminFavoriteFilterMode,
                      adminSortMode,
                      setAdminSortMode,
                      adminSearch,
                      setAdminSearch,
                      adminHidePassed,
                      setAdminHidePassed,
                      handleAdminPitchExport,
                      MultiSelectDropdown,
                    }}
                  />
                </>
              );
            }
            return (
            <>
              {paneHeader}

              <div className="admin-pane-body">
              {/* Filter bar — styling lives in `.filter-bar` utility class.
                  Controls use the global input/select bevel styling from win95-base.css. */}
              <div className="filter-bar filter-bar--one-row" role="group" aria-label="Filter and sort reviews">
                {isSuperAdmin && (
                  <select
                    aria-label="Filter by chapter"
                    value={adminChapterFilter}
                    onChange={(e) => setAdminChapterFilter(e.target.value)}
                  >
                    <option value="">All chapters</option>
                    {[...new Set(adminPitches.map((p) => p.chapter).filter(Boolean))].sort().map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                )}
                <MultiSelectDropdown
                  options={[...new Set(adminPitches.map((p) => p.quarter).filter(Boolean))]
                    .sort((a, b) => {
                      const [aQ, aY] = a.split(' ');
                      const [bQ, bY] = b.split(' ');
                      if (bY !== aY) return parseInt(bY) - parseInt(aY);
                      return parseInt(bQ.substring(1)) - parseInt(aQ.substring(1));
                    })}
                  selected={adminQuarterFilter}
                  onChange={setAdminQuarterFilter}
                  placeholder="All quarters"
                />
                <select
                  aria-label="Filter by rating"
                  value={adminFavoriteFilterMode}
                  onChange={(e) => setAdminFavoriteFilterMode(e.target.value)}
                >
                  <option value="all">All ratings</option>
                  <option value="favsOnly">Favorites only</option>
                  <option value="favsAndCons">Favs &amp; considerations</option>
                  <option value="shortlisted">Shortlisted only</option>
                </select>
                <select
                  aria-label="Sort order"
                  value={adminSortMode}
                  onChange={(e) => setAdminSortMode(e.target.value)}
                  disabled={adminFavoriteFilterMode === 'favsOnly' || adminFavoriteFilterMode === 'favsAndCons'}
                  title={adminFavoriteFilterMode === 'favsOnly' || adminFavoriteFilterMode === 'favsAndCons'
                    ? 'Sort is fixed to favorite count while filtering by favorites'
                    : 'Sort order'}
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="avgDesc">Wtd avg ↓</option>
                  <option value="avgAsc">Wtd avg ↑</option>
                  <option value="sumDesc">Total score ↓</option>
                  <option value="sumAsc">Total score ↑</option>
                  <option value="mostReviews">Most reviews</option>
                </select>
                <input
                  type="search"
                  aria-label="Search pitches"
                  placeholder="Search name, business, email..."
                  value={adminSearch}
                  onChange={(e) => setAdminSearch(e.target.value)}
                />
                <button
                  type="button"
                  className="retro-toggle"
                  aria-pressed={adminHidePassed}
                  onClick={() => setAdminHidePassed(!adminHidePassed)}
                  title={adminHidePassed ? "Show pitches with only Pass/Ineligible reviews" : "Hide pitches with only Pass/Ineligible reviews"}
                >
                  {adminHidePassed ? 'Show passes' : 'Hide passes'}
                </button>
                <RetroButton onClick={handleAdminPitchExport} title="Download current results as CSV">
                  Export
                </RetroButton>
                <span
                  className="filter-bar__count"
                  aria-live="polite"
                  title="Showing filtered pitches out of total"
                >
                  <strong>{adminFilteredSortedPitches.length}</strong> of {adminPitches.length}
                </span>
              </div>

              {/* Admin Pitches List - Uses adminFilteredSortedPitches */}
              {adminFilteredSortedPitches.length === 0 && (
                <EmptyState
                  icon={null}
                  title="No pitches match"
                  description="Try clearing a filter or widening your search."
                />
              )}
              {adminFilteredSortedPitches.map((p) => {
                const groupedReviews = getGroupedReviewsForAdmin(p.id);
                const formattedAdminDate = formatDate(p.createdAt || p.createdDate);
                return (
                  <div key={p.id} className="admin-pitch-card">
                    {/* Top Section: Info & Actions */}
                    <div className="admin-pitch-card__head">
                      {/* Pitch Info */}
                      <div className="admin-pitch-card__info">
                        <div className="admin-pitch-card__title">
                          <strong>{p.businessName || 'N/A'}</strong>
                          <span className="admin-pitch-card__by"> by {p.founderName || 'N/A'}</span>
                        </div>
                        {p.aiSummary
                          ? <div className="admin-pitch-card__summary">{p.aiSummary}</div>
                          : <div className="admin-pitch-card__summary admin-pitch-card__summary--fallback"><em>{p.valueProp?.substring(0, 120)}{p.valueProp?.length > 120 ? '…' : ''}</em></div>
                        }
                        <div className="admin-pitch-card__meta">
                          <span>{p.chapter || 'N/A'}</span>
                          <span className="admin-pitch-card__meta-sep">·</span>
                          <span style={{ fontFamily: 'var(--font-numeral)' }}>{p.quarter || 'N/A'}</span>
                          <span className="admin-pitch-card__meta-sep">·</span>
                          <span>Submitted {formattedAdminDate}</span>
                        </div>
                        <div className="admin-pitch-card__tags">
                          {p.isContest && <RetroPill tone="purple">Contest submission</RetroPill>}
                          {p.isWinner && <RetroPill tone="green" icon={<StatusIcon type="trophy" size={14} />}>Grant winner</RetroPill>}
                          {p.shortlisted && (
                            <RetroPill tone="yellow" title={p.shortlistedBy ? `Shortlisted by ${p.shortlistedBy}` : 'On the group shortlist'}>
                              Shortlisted
                            </RetroPill>
                          )}
                        </div>
                      </div>
                      {/* Action */}
                      <div className="admin-pitch-card__actions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <RetroButton
                          size="sm"
                          variant={p.shortlisted ? 'primary' : 'default'}
                          onClick={() => handleToggleShortlist(p.id, p.shortlisted)}
                          disabled={shortlistTogglingId === p.id}
                          title={p.shortlisted ? 'Remove from the group shortlist' : 'Add to the group shortlist for discussion'}
                        >
                          {p.shortlisted ? 'Shortlisted' : 'Shortlist'}
                        </RetroButton>
                        <RetroButton
                          size="sm"
                          onClick={() => setExpandedPitchId(expandedPitchId === p.id ? null : p.id)}
                          aria-expanded={expandedPitchId === p.id}
                        >
                          {expandedPitchId === p.id ? 'Hide details' : 'Expand details'}
                        </RetroButton>
                      </div>
                    </div>
                    {/* Review Summary Section */}
                    {groupedReviews.count > 0 && (
                      <div className="admin-pitch-card__reviews">
                        <div className="admin-pitch-card__reviews-label">
                          LP reviews (<span style={{ fontFamily: 'var(--font-numeral)' }}>{groupedReviews.count}</span>)
                        </div>
                        <div className="admin-pitch-card__reviews-chips">
                          {Object.entries(groupedReviews.byRating)
                            .sort(([ratingA], [ratingB]) => {
                              const order = { Favorite: 1, Consideration: 2, Pass: 3, Ineligible: 4, 'No Rating': 5 };
                              return (order[ratingA] || 99) - (order[ratingB] || 99);
                            })
                            .map(([rating, data]) => (
                              <RetroPill
                                key={rating}
                                tone={ratingTonePill(rating)}
                                icon={<ReviewRatingIcon rating={rating} size={14} />}
                                title={`Reviewers: ${data.reviewers.join(', ')}`}
                              >
                                {rating}: <span style={{ fontFamily: 'var(--font-numeral)', marginLeft: 2 }}>{data.count}</span>
                              </RetroPill>
                            ))}
                          {groupedReviews.scoredCount > 0 && (
                            <span
                              className="admin-pitch-card__score"
                              title={`Favorite +2, Consideration +1, Pass 0, Ineligible -2.\navg = Bayesian-weighted (used to sort) across ${groupedReviews.scoredCount} rated review(s).\nraw = unweighted average.`}
                            >
                              Score <strong style={{ fontFamily: 'var(--font-numeral)' }}>
                                {groupedReviews.score > 0 ? `+${groupedReviews.score}` : groupedReviews.score}
                              </strong>
                              <span className="admin-pitch-card__score-avg">
                                avg <span style={{ fontFamily: 'var(--font-numeral)' }}>{groupedReviews.weightedAverage.toFixed(2)}</span>
                                {' '}· raw <span style={{ fontFamily: 'var(--font-numeral)' }}>{groupedReviews.averageScore.toFixed(2)}</span>
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {groupedReviews.count === 0 && (
                      <div className="admin-pitch-card__reviews admin-pitch-card__reviews--empty">
                        No LP reviews submitted yet.
                      </div>
                    )}
                    {/* Expanded Details Section (Conditional) */}
                    {expandedPitchId === p.id && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--mb-ink-15)' }}>
                        {/* Grant Winner controls */}
                        <div style={{
                          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                          gap: 10, marginBottom: 14, fontSize: 12, color: 'var(--mb-ink-60)',
                          fontFamily: 'var(--font-chrome)', letterSpacing: ' 0.01em', textTransform: 'none',
                        }}>
                          <span>Grant winner status</span>
                          <RetroButton
                            size="sm"
                            variant={p.isWinner ? 'default' : 'primary'}
                            onClick={() => handleAssignWinner(p.id, p.isWinner)}
                          >
                            {p.isWinner ? 'Remove winner' : 'Assign winner'}
                          </RetroButton>
                        </div>
                        <div style={{ display: 'flex', gap: '15px', flexWrap:'wrap' }}>
                        {/* Pitch Details */}
                        <div style={{ flex: '1 1 50%', minWidth: '300px' }}>
                          <h5 style={{ margin: '0 0 8px 0', color: 'var(--mb-ink)', fontSize: '14px', fontWeight: 'bold' }}>Full pitch details</h5>
                          <div style={{ padding: '20px', maxHeight:'500px', overflowY:'auto', background: 'var(--gnf-bg)', border: '1px solid var(--gnf-bg-silver)' }}>
                            {/* Basic Info */}
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Founder:</span>
                              <span style={{ color: 'var(--mb-ink)', fontSize: '14px', fontWeight: '500' }}>{p.founderName || "N/A"}</span>
                            </div>
                            
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Email:</span>
                              <span style={{ color: 'var(--mb-ink)', fontSize: '14px' }}>{p.email || "N/A"}</span>
                            </div>
                            
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Website:</span>
                              <span style={{ color: 'var(--mb-ink)', fontSize: '14px' }}>{p.website || "N/A"}</span>
                            </div>
                            
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Paying Customers:</span>
                              <span style={{ color: p.hasPayingCustomers ? 'var(--green-dark)' : 'var(--mb-ink-60)', fontSize: '14px', fontWeight: p.hasPayingCustomers ? '600' : 'normal' }}>
                                {p.hasPayingCustomers ? 'Yes' : 'No'}
                              </span>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                              <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Heard About:</span>
                              <span style={{ color: 'var(--mb-ink)', fontSize: '14px' }}>{p.heardAbout || "N/A"}</span>
                            </div>

                            {/* Business Details - Less sectioned */}
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginBottom: '6px' }}>Value Proposition</div>
                              <div style={{ color: 'var(--mb-ink)', fontSize: '14px', lineHeight: '1.6' }}>{p.valueProp || "N/A"}</div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginBottom: '6px' }}>Problem</div>
                              <div style={{ color: 'var(--mb-ink)', fontSize: '14px', lineHeight: '1.6' }}>{p.problem || "N/A"}</div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginBottom: '6px' }}>Solution</div>
                              <div style={{ color: 'var(--mb-ink)', fontSize: '14px', lineHeight: '1.6' }}>{p.solution || "N/A"}</div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginBottom: '6px' }}>Business Model</div>
                              <div style={{ color: 'var(--mb-ink)', fontSize: '14px', lineHeight: '1.6' }}>{p.businessModel || "N/A"}</div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginBottom: '6px' }}>Grant Fund Use</div>
                              <div style={{ color: 'var(--mb-ink)', fontSize: '14px', lineHeight: '1.6' }}>{p.grantUsePlan || "N/A"}</div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginBottom: '6px' }}>Founder Bio</div>
                              <div style={{ color: 'var(--mb-ink)', fontSize: '14px', lineHeight: '1.6' }}>{p.bio || "N/A"}</div>
                            </div>

                            <div style={{ borderTop: '1px solid var(--gnf-bg-gray)', paddingTop: '16px', marginTop: '20px' }}>
                              <div style={{ marginBottom: '12px' }}>
                                <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Zip Code:</span>
                                <span style={{ color: 'var(--mb-ink)', fontSize: '14px' }}>{p.zipCode || "N/A"}</span>
                              </div>

                              <div style={{ marginBottom: '12px' }}>
                                <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Video URL:</span>
                                <span style={{ color: 'var(--mb-ink)', fontSize: '14px' }}>{p.pitchVideoUrl || "N/A"}</span>
                              </div>

                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginBottom: '6px' }}>Self Identification Tags:</div>
                                <div style={{ color: 'var(--mb-ink)', fontSize: '14px' }}>{p.selfIdentification?.join(", ") || "N/A"}</div>
                              </div>

                              <div style={{ marginBottom: '12px' }}>
                                <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>Terms & Privacy Agreed:</span>
                                <span style={{ color: p.consentToShare ? 'var(--green-dark)' : 'var(--mb-ink-60)', fontSize: '14px', fontWeight: p.consentToShare ? '600' : 'normal' }}>
                                  {p.consentToShare ? 'Yes' : 'No'}
                                </span>
                              </div>

                              <div>
                                <span style={{ color: 'var(--mb-ink-60)', fontSize: '13px', marginRight: '10px' }}>In-Person Meetup Agreed:</span>
                                <span style={{ color: p.consentToMeetup ? 'var(--green-dark)' : 'var(--mb-ink-60)', fontSize: '14px', fontWeight: p.consentToMeetup ? '600' : 'normal' }}>
                                  {p.consentToMeetup ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Aggregated Comments Added */}
                        <div style={{ flex: '1 1 40%', minWidth: '250px' }}>
                          <h5 style={{ margin: '0 0 8px 0', color: 'var(--mb-ink)', fontSize: '14px', fontWeight: 'bold' }}>LP review comments ({groupedReviews.comments.length})</h5>
                          <div style={{ padding: '20px', maxHeight:'500px', overflowY:'auto', background: 'var(--gnf-bg)', border: '1px solid var(--gnf-bg-silver)' }}>
                            {groupedReviews.comments.length > 0 ? (
                                groupedReviews.comments.map((comment, index) => (
                                    <div key={index} style={{
                                      padding: '12px 0',
                                      borderBottom: index < groupedReviews.comments.length - 1 ? '1px solid var(--gnf-bg-gray)' : 'none',
                                    }}>
                                      <div style={{
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        color: 'var(--mb-ink-60)',
                                        marginBottom: '4px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px'
                                      }}>
                                        {comment.reviewer}
                                      </div>
                                      <div style={{
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        color: 'var(--mb-ink)'
                                      }}>
                                        {comment.text}
                                      </div>
                                    </div>
                                ))
                            ) : (
                                <p style={{ fontStyle: 'italic', color: 'var(--mb-ink-60)', textAlign: 'center', padding: '20px' }}>No comments provided in reviews.</p>
                            )}
                          </div>

                          {/* Admin discussion notes — visible only to chapter directors and
                              superAdmins. Captures follow-ups and live discussion during the
                              quarterly review meeting. Anyone with admin access can add notes
                              under their own name; authors can edit or delete their own. */}
                          {(() => {
                            const notesForPitch = adminNotesByPitch[p.id] || [];
                            return (
                              <div style={{ marginTop: 14 }}>
                                <h5 style={{ margin: '0 0 8px 0', color: 'var(--mb-ink)', fontSize: '14px', fontWeight: 'bold' }}>
                                  Admin discussion notes ({notesForPitch.length})
                                </h5>
                                <div style={{ padding: '20px', background: 'var(--gnf-bg)', border: '1px solid var(--gnf-bg-silver)' }}>
                                  {notesForPitch.length > 0 ? (
                                    notesForPitch.map((note, index) => {
                                      const isAuthor = note.authorId === user.uid;
                                      const canManage = isAuthor || isSuperAdmin;
                                      const isEditing = editingAdminNoteId === note.id;
                                      const timestampLabel = formatDate(note.createdAt);
                                      const edited = (note.editCount || 0) > 0 ? ' · edited' : '';
                                      return (
                                        <div key={note.id} style={{
                                          padding: '12px 0',
                                          borderBottom: index < notesForPitch.length - 1 ? '1px solid var(--gnf-bg-gray)' : 'none',
                                        }}>
                                          <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'baseline',
                                            gap: 8,
                                            marginBottom: 4,
                                          }}>
                                            <div style={{
                                              fontSize: '11px',
                                              fontWeight: '600',
                                              color: 'var(--mb-ink-60)',
                                              textTransform: 'none',
                                              letterSpacing: '0.3px',
                                            }}>
                                              {note.authorName || 'Admin'} <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>· {timestampLabel}{edited}</span>
                                            </div>
                                            {canManage && !isEditing && (
                                              <div style={{ display: 'flex', gap: 6 }}>
                                                <button
                                                  type="button"
                                                  onClick={() => handleStartEditAdminNote(note)}
                                                  style={{ background: 'transparent', border: 'none', color: 'var(--mb-ink-60)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                                >
                                                  Edit
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => handleDeleteAdminNote(note.id)}
                                                  disabled={adminNoteSavingId === note.id}
                                                  style={{ background: 'transparent', border: 'none', color: 'var(--mb-ink-60)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          {isEditing ? (
                                            <div>
                                              <textarea
                                                value={editingAdminNoteText}
                                                onChange={(e) => setEditingAdminNoteText(e.target.value)}
                                                rows={3}
                                                style={{ width: '100%', fontFamily: 'inherit', fontSize: 14, padding: 8, boxSizing: 'border-box', border: '2px solid', borderColor: 'var(--bevel-sunken)', boxShadow: 'none', background: 'var(--gnf-bg)', resize: 'vertical' }}
                                                aria-label="Edit note"
                                              />
                                              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                                <RetroButton
                                                  size="sm"
                                                  variant="primary"
                                                  onClick={() => handleSaveEditAdminNote(note.id)}
                                                  disabled={adminNoteSavingId === note.id || !editingAdminNoteText.trim()}
                                                >
                                                  {adminNoteSavingId === note.id ? 'Saving…' : 'Save'}
                                                </RetroButton>
                                                <RetroButton size="sm" onClick={handleCancelEditAdminNote}>
                                                  Cancel
                                                </RetroButton>
                                              </div>
                                            </div>
                                          ) : (
                                            <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--mb-ink)', whiteSpace: 'pre-wrap' }}>
                                              {note.text}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <p style={{ fontStyle: 'italic', color: 'var(--mb-ink-60)', textAlign: 'center', padding: '4px 0', margin: 0 }}>
                                      No notes yet.
                                    </p>
                                  )}
                                  <div style={{ marginTop: notesForPitch.length > 0 ? 12 : 0, paddingTop: notesForPitch.length > 0 ? 12 : 0, borderTop: notesForPitch.length > 0 ? '1px solid var(--gnf-bg-gray)' : 'none' }}>
                                    <textarea
                                      value={adminNoteDrafts[p.id] || ''}
                                      onChange={(e) => setAdminNoteDrafts(prev => ({ ...prev, [p.id]: e.target.value }))}
                                      placeholder="Add a note from the discussion — follow-ups, questions, a quote you want to remember."
                                      rows={3}
                                      style={{ width: '100%', fontFamily: 'inherit', fontSize: 14, padding: 8, boxSizing: 'border-box', border: '2px solid', borderColor: 'var(--bevel-sunken)', boxShadow: 'none', background: 'var(--gnf-bg)', resize: 'vertical' }}
                                      aria-label="Add admin note"
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                                      <RetroButton
                                        size="sm"
                                        variant="primary"
                                        onClick={() => handleAddAdminNote(p)}
                                        disabled={adminNoteSavingId === p.id || !(adminNoteDrafts[p.id] || '').trim()}
                                      >
                                        {adminNoteSavingId === p.id ? 'Saving…' : 'Add note'}
                                      </RetroButton>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </>
            );
}
